import { GenericCampaignCreationPropertiesType } from '@ads/front-core/api'
import { Extensions } from '../interfaces'

export interface IGenericCreationFileParameter {
  extensions: Extensions[]
  maxFileSize: string
  maxFileSizeInBytes: number
  type: 'IMAGE' | 'ZIP' | 'TEXT'
}

export interface IGenericCreationParameters {
  dimension: string
  type: 'IMAGE' | 'ZIP' | 'TEXT' | 'ANY' | 'WALLPAPER'
  fileParameters: IGenericCreationFileParameter[]
}

const DEFAULT_IMAGE_EXTENSIONS = [
  Extensions.PNG,
  Extensions.JPG,
  Extensions.JPEG,
]

const DEFAULT_IMAGE_WITH_GIF_EXTENSIONS = [
  ...DEFAULT_IMAGE_EXTENSIONS,
  Extensions.GIF,
]

const createImageFileParameters = (
  maxFileSizeInKb: number,
  extensions: Extensions[] = DEFAULT_IMAGE_EXTENSIONS,
): IGenericCreationFileParameter => {
  return {
    extensions,
    maxFileSize: `${maxFileSizeInKb}kB`,
    maxFileSizeInBytes: 1024 * maxFileSizeInKb,
    type: 'IMAGE',
  }
}

const zipFileParameters: IGenericCreationFileParameter = {
  extensions: [Extensions.ZIP],
  maxFileSize: '150kB',
  maxFileSizeInBytes: 1024 * 150,
  type: 'ZIP',
}

export const genericCreationParameters: Record<
  GenericCampaignCreationPropertiesType,
  IGenericCreationParameters
> = {
  VIDEO: {
    dimension: 'mp4, 1920x1080 / 16:9, 1024x576 / 16:9, 1280x720 / 16:9',
    type: 'IMAGE',
    fileParameters: [
      {
        extensions: [Extensions.MP4],
        maxFileSize: '5MB',
        maxFileSizeInBytes: 1024 * 1024 * 5,
        type: 'IMAGE',
      },
    ],
  },
  BAR: {
    dimension: '300x50',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(30)],
  },
  BAR_LP_WP: {
    dimension: '300x60',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(30)],
  },
  STICKY: {
    dimension: '320x50',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(30)],
  },
  CONTENT_BOX_MEDIUM: {
    dimension: '940x300',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(60)],
  },
  CONTENT_BOX_LARGE: {
    dimension: '1260x300',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(150)],
  },
  BANNER_300x250: {
    dimension: '300x250',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(60), zipFileParameters],
  },
  BANNER_300x600: {
    dimension: '300x600',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(60), zipFileParameters],
  },
  BANNER_750x200: {
    dimension: '750x200',
    type: 'IMAGE',
    fileParameters: [
      createImageFileParameters(60, [
        ...DEFAULT_IMAGE_EXTENSIONS,
        Extensions.GIF,
      ]),
      zipFileParameters,
    ],
  },
  BANNER_350x216: {
    dimension: '350x216',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(50)],
  },
  CUBE_3D: {
    dimension: undefined,
    type: 'ZIP',
    fileParameters: [zipFileParameters],
  },
  MOTION_AD: {
    dimension: undefined,
    type: 'ZIP',
    fileParameters: [zipFileParameters],
  },
  SCRATCH: {
    dimension: undefined,
    type: 'ZIP',
    fileParameters: [zipFileParameters],
  },
  SLASH: {
    dimension: undefined,
    type: 'ZIP',
    fileParameters: [zipFileParameters],
  },
  RECTANGLE_300x250: {
    dimension: '300x250',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(40), zipFileParameters],
  },
  MEGA_DOUBLE_BILLBOARD_970x300: {
    dimension: '970x300',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(70), zipFileParameters],
  },
  DOUBLE_BILLBOARD_750x200: {
    dimension: '750x200',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(50), zipFileParameters],
  },
  HALFPAGE_300x600: {
    dimension: '300x600',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(60), zipFileParameters],
  },
  TRIPLE_BILLBOARD_750x300: {
    dimension: '750x300',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(60), zipFileParameters],
  },
  BANNER_300x100: {
    dimension: '300x100',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(20), zipFileParameters],
  },
  WIDEBOARD_970x200: {
    dimension: '970x200',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(60), zipFileParameters],
  },
  SKYSCRAPER_160x600: {
    dimension: '160x600',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(40), zipFileParameters],
  },
  BILLBOARD_750x100: {
    dimension: '750x100',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(40), zipFileParameters],
  },
  SCALABLE_XL_BANNER_600x400: {
    dimension: '600x400',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(40), zipFileParameters],
  },
  ANY_FILE_RESOURCE: {
    dimension: null,
    type: 'ANY',
    fileParameters: [],
  },
  ANY_ARTICLE_RESOURCE: {
    dimension: null,
    type: 'ANY',
    fileParameters: [],
  },
  ARTICLE_CONTENT_RESOURCE: {
    dimension: null,
    type: 'TEXT',
    fileParameters: [
      {
        extensions: [
          Extensions.DOC,
          Extensions.DOCX,
          Extensions.PDF,
          Extensions.TXT,
        ],
        maxFileSize: '5MB',
        maxFileSizeInBytes: 1024 * 1024 * 5,
        type: 'TEXT',
      },
    ],
  },
  BANNER_1200x600: {
    dimension: '1200x600',
    type: 'IMAGE',
    fileParameters: [
      createImageFileParameters(150, DEFAULT_IMAGE_WITH_GIF_EXTENSIONS),
      zipFileParameters,
    ],
  },
  BANNER_1920x870: {
    dimension: '1920x870',
    type: 'IMAGE',
    fileParameters: [
      createImageFileParameters(150, DEFAULT_IMAGE_WITH_GIF_EXTENSIONS),
      zipFileParameters,
    ],
  },
  BANNER_320x370: {
    dimension: '320x370',
    type: 'IMAGE',
    fileParameters: [
      createImageFileParameters(60, DEFAULT_IMAGE_WITH_GIF_EXTENSIONS),
    ],
  },
  BANNER_360x600: {
    dimension: '360x600',
    type: 'IMAGE',
    fileParameters: [
      createImageFileParameters(60, DEFAULT_IMAGE_WITH_GIF_EXTENSIONS),
    ],
  },
  BANNER_640x280: {
    dimension: '640x280',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(60), zipFileParameters],
  },
  BANNER_1260x300: {
    dimension: '1260x300',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(60), zipFileParameters],
  },
  BANNER_1260x600: {
    dimension: '1260x600',
    type: 'IMAGE',
    fileParameters: [
      createImageFileParameters(100),
      {
        extensions: [Extensions.ZIP],
        maxFileSize: '300kB',
        maxFileSizeInBytes: 1024 * 300,
        type: 'ZIP',
      },
    ],
  },
  BANNER_940x300: {
    dimension: '940x300',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(60), zipFileParameters],
  },
  BANNER_940x600: {
    dimension: '940x600',
    type: 'IMAGE',
    fileParameters: [
      createImageFileParameters(100),
      {
        extensions: [Extensions.ZIP],
        maxFileSize: '300kB',
        maxFileSizeInBytes: 1024 * 300,
        type: 'ZIP',
      },
    ],
  },
  BANNER_600x200: {
    dimension: '600x200',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(40), zipFileParameters],
  },
  BANNER_600x400: {
    dimension: '600x400',
    type: 'IMAGE',
    fileParameters: [createImageFileParameters(40), zipFileParameters],
  },
  BANNER_1200x1080: {
    dimension: '1200x1080',
    type: 'WALLPAPER',
    fileParameters: [createImageFileParameters(150)],
  },
  BANNER_1226x1080: {
    dimension: '1226x1080',
    type: 'WALLPAPER',
    fileParameters: [createImageFileParameters(150)],
  },
  BANNER_1266x1080: {
    dimension: '266x1080',
    type: 'WALLPAPER',
    fileParameters: [createImageFileParameters(150)],
  },
  BANNER_1280x1080: {
    dimension: '1280x1080',
    type: 'WALLPAPER',
    fileParameters: [createImageFileParameters(150)],
  },
  BANNER_1320x1080: {
    dimension: '1320x1080',
    type: 'WALLPAPER',
    fileParameters: [createImageFileParameters(150)],
  },
  BANNER_1366x1080: {
    dimension: '1366x1080',
    type: 'WALLPAPER',
    fileParameters: [createImageFileParameters(150)],
  },
  BANNER_1800x1080: {
    dimension: '1800x1080',
    type: 'WALLPAPER',
    fileParameters: [createImageFileParameters(150)],
  },
  BANNER_1920x1080: {
    dimension: '1920x1080',
    type: 'WALLPAPER',
    fileParameters: [createImageFileParameters(150)],
  },
  BANNER_970x200: {
    dimension: '970x200',
    type: 'WALLPAPER',
    fileParameters: [createImageFileParameters(60), zipFileParameters],
  },
}

export const getGenericCreationParameters = (
  type: GenericCampaignCreationPropertiesType,
): IGenericCreationParameters => genericCreationParameters[type]

export const getGenericCreationDimension = (
  types: GenericCampaignCreationPropertiesType[],
): string =>
  types.map(type => genericCreationParameters[type].dimension).join(', ')
